/**
 * @Author: v_jppjpeng
 * @Date: 2021-11-30 16:34:12
 * @Explain: 接口调用工具类
 */
import { intlGet, intlGetApiError, intlGetNestApiError } from '@/locales';
import type {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import moment from 'moment';
import { createSearchParams, getDvaApp, history } from 'umi';
import { v4 as uuidv4 } from 'uuid';
import { isDev } from '../config';
// router
import RouterPath from '@/router/routingPath';
// utils
import { getLocationQuery, onJoinData } from '@/utils/common';
import { isN } from '@/utils/common/dataJudgment';
import type { convertKeysToUnderscoreParamsTypes } from '@/utils/format/letter';
import { convertKeysToUnderscore } from '@/utils/format/letter';
import { getUrlParams, onSerialize } from '@/utils/format/url';
import { onPattern, pattern } from '@/utils/regular';
// type
import { BooleanTypeEnum } from '@/definition/common/data.d';
import type { ConnectState } from '@/models/connect.d';
import { ReducersEnum } from '@/models/connect.d';
// components
import { notification } from '@/components/tdesign';
// utils
import { LoginStatusEnum } from '@/pages/adminManage/data.d';
import { EventTypeEnum } from '@/utils/aegisMonitor/typing';
import burialPoint, { eventNameEnum } from '@/utils/burialPoint';
import { jsonParse } from '@/utils/global';

// axios 链接前缀
export const axiosBaseURL = '/capi/v2';
// 请求前标记 时间撮
export const beforeReqTimeMark = 'beforeReqTimeMark';
// 控制台打印封装
export function consoleLog({
  title,
  outText,
  color,
}: {
  title: string;
  outText: any;
  color: string;
}): any {
  const content = [`%c ${title}-----------:`, `color: ${color}`, outText];

  if (isDev) {
    console.log(...content);
  }
}

// 账号未登录
/**  不需要验证登录的页面 */
const whiteList = [
  RouterPath.LOGIN,
  RouterPath.REGISTER,
  RouterPath.FORGOT_PASSWORD,
  RouterPath.DISCORD__AUTHORIZE,
];

// nest接口状态码都不上报
const nestWhiteCode = [400, 401];

// 拼接链接
export const getUrl = (url: string, lang: string) => {
  const params = getUrlParams(url);
  const returnUrl = getLocationQuery('returnUrl');
  params.lang = lang;

  params.return_url = encodeURIComponent(returnUrl || window.location.href);
  return `${url}?${createSearchParams(params).toString()}`;
};

export const getHeaders = () => {
  const { dispatch, getState } = getDvaApp()._store;
  const { CommonModel }: ConnectState = getState();
  const { loginType } = CommonModel;
  // 用户信息
  const userNameLink = getLocationQuery('user');
  const { userInfo } = CommonModel;
  // 业务信息（左侧菜单-项目）
  const { currentBusiness } = CommonModel;
  // 角色信息
  const roles: string[] = currentBusiness?.roles || [];
  // token
  const accessTokenLink = getLocationQuery('access_token');
  const accessToken = accessTokenLink || CommonModel.accessToken;

  // 如果链接上有token，则存储
  if (accessTokenLink) {
    dispatch({
      type: ReducersEnum.SET_TOKEN,
      payload: {
        accessToken,
      },
    });
  }
  // 清除旧的token，当超过700个字符代表token的算法是以前(RS256)，过渡方案
  if (accessToken && accessToken.length > 700) {
    dispatch({
      type: ReducersEnum.ON_LOG_OUT,
    });
  }
  // 如果链接上有用户信息，则存储
  // if (userNameLink) {
  //   dispatch({
  //     type: ReducersEnum.SET_USER_INFO,
  //     payload: {
  //       accountId: '',
  //       accountName: userNameLink,
  //     },
  //   });
  // }

  return {
    'X-User-id': userInfo.accountId,
    'X-User-name': userInfo.accountName,
    'X-Service-Type': currentBusiness?.gameId,
    'X-User-Role': onJoinData(roles),
    'X-Request-Id': uuidv4(),
    'Access-Token': accessToken,
    // accessToken,
    loginType,
  };
};

// 协议是否进行转化
const switchRequestKeyRule = ({
  data,
  switchToUnderline,
  ignoreConvertedKey = [],
  ignoreConvertedValueKey = [],
}: convertKeysToUnderscoreParamsTypes) => {
  let resultData;
  resultData = switchToUnderline
    ? convertKeysToUnderscore({
        data,
        ignoreConvertedKey,
        ignoreConvertedValueKey,
      }) // 入参：驼峰 -> 下划线
    : convertKeysToUnderscore({
        data,
        switchToUnderline,
        ignoreConvertedKey,
        ignoreConvertedValueKey,
      }); // 入参：下划线 -> 驼峰

  return resultData;
};

// 请求前 - 设置请求的参数
export const setReqConfig: any = (config: InternalAxiosRequestConfig) => {
  const { switchReqToUnderline } = config?.params || {};
  const { getState } = getDvaApp()._store;
  const { CommonModel }: ConnectState = getState();
  // 参数不做驼峰和下划线互转换的key
  const ignoreConvertedKey = config?.params?.ignoreConvertedKey
    ? jsonParse(config?.params?.ignoreConvertedKey)
    : [];
  // 参数value对应的key需要转换，而value无需转换;（备注：ignoreConvertedValueKey优先级大于ignoreConvertedKey）
  const ignoreConvertedValueKey = config?.params?.ignoreConvertedValueKey
    ? jsonParse(config?.params?.ignoreConvertedValueKey)
    : [];

  // isPackage 是否包装一层，N：为否 特殊格式处理
  config.data = isN(config?.params?.isPackage)
    ? config.data
    : { data: config.data };
  config.url =
    config.url && !config?.params?.noGetUrl
      ? getUrl(config.url, CommonModel.localLanguage)
      : config.url;
  // @ts-ignore
  config.headers = {
    time: moment().valueOf(),
    timeStr: moment().format('YYYY-MM-DD HH:mm:ss.SSS'),
    ...config.headers,
    ...getHeaders(),
  };

  // 协议是否进行转化
  if (switchReqToUnderline) {
    let reqSource: any;
    if (config?.data) {
      reqSource = { ...config.data };
    } else if (config?.params) {
      reqSource = { ...config.params };
    }
    const data = switchRequestKeyRule({
      data: reqSource,
      switchToUnderline: true,
      ignoreConvertedKey,
      ignoreConvertedValueKey,
    });

    if (config.data) {
      config.data = data;
    } else {
      config.params = { ...config.params, ...data };
    }
  }

  return config;
};

// 请求后 - 成功处理
export const onResSuccess = (response: AxiosResponse) => {
  // 超过5s 上报
  try {
    // 请求时间 毫秒级别
    const reqPendingTime =
      new Date().valueOf() - response.config[beforeReqTimeMark];
    // 超过5s 处理
    if (reqPendingTime > 5000) {
      const msg = `超过5s请求耗时：${reqPendingTime} ms`;
      // 超过5s 上报
      burialPoint.reportEvent(
        {
          moduleName: 'requestExceedFiveSeconds',
          eventType: EventTypeEnum.REQUEST_EVENT,
          eventName: eventNameEnum[10],
          msg,
          exts: {
            errorMsg: msg,
            url: response?.config?.url,
            data: response?.config?.data,
            headers: response?.config?.headers,
          },
        },
        {
          // 额外的参数 aegis 目前只支持ext1 ext2 ext3 待调试
          api: response?.request?.aegisUrl,
          method: response?.request?.aegisMethod,
          data: response?.config?.data?.toString(),
          errorMsg: msg,
          msg,
        },
      );
    }
  } catch (e) {
    console.error(e);
  }

  const { switchReqToUnderline } = response?.config?.params || {};
  const { dispatch } = getDvaApp()._store;
  // 参数不做驼峰和下划线互转换的key
  const ignoreConvertedKey = response?.config?.params?.ignoreConvertedKey
    ? jsonParse(response?.config?.params?.ignoreConvertedKey)
    : [];
  // 参数value无需转换的key;（备注ignoreConvertedValueKey优先级大于ignoreConvertedKey）
  const ignoreConvertedValueKey = response?.config?.params
    ?.ignoreConvertedValueKey
    ? jsonParse(response?.config?.params?.ignoreConvertedValueKey)
    : [];

  let resData = response.data;
  const resCode = resData.code;
  const accessToken = response.headers['access-token'];
  // 登录成功后访问接口没权限
  if (resCode === 1001) {
    const { getState } = getDvaApp()._store;
    const { CommonModel }: ConnectState = getState();
    const { loginStatus, businessList } = CommonModel;
    if (
      ([LoginStatusEnum.LOGIN_FAIL, LoginStatusEnum.NOT_LOGGED_IN].every(
        (status) => status !== loginStatus,
      ) &&
        !businessList) ||
      (Array.isArray(businessList) && !businessList.length)
    ) {
      // 跳转无权限页面
      history.push(getUrl(RouterPath.NO_AUTH_PAGE, ''));
    }
  }

  if (resCode !== 0) {
    const { config } = response;
    // 请求上报
    burialPoint.aegisReportEvent(
      {
        eventName: `${resCode}-----${config.url}` || window.location.pathname,
      },
      {
        ext1: config.data,
        ext2: config.headers,
        ext3: resData,
      },
    );
  }

  // 协议是否进行转化
  if (switchReqToUnderline) {
    resData = switchRequestKeyRule({
      data: response.data,
      switchToUnderline: false,
      ignoreConvertedKey,
      ignoreConvertedValueKey,
    });
    // 判断该接口为分页list接口，分页信息增加pageInfo包裹
    if (
      [
        resData.data?.pageNum,
        resData.data?.pageSize,
        resData.data?.total,
        resData.data?.totalPage,
      ].some((i) => onPattern(i, pattern.isNumber))
    ) {
      resData.data.pageInfo = {};
      if (resData?.data?.pageNum)
        resData.data.pageInfo.pageNum = resData.data.pageNum;
      if (resData?.data?.pageSize)
        resData.data.pageInfo.pageSize = resData.data.pageSize;
      if (resData?.data?.total >= 0)
        resData.data.pageInfo.total = resData.data.total;
      if (resData?.data?.totalPage >= 0)
        resData.data.pageInfo.totalPage = resData.data.totalPage;
    }
  }

  if (
    resData?.ret &&
    resData.ret === 401 &&
    !whiteList.includes(location.pathname)
  ) {
    // notification.error({ description: intlGet("ADMIN.LOGIN.FAILURE") })
    dispatch({
      type: ReducersEnum.ON_LOG_OUT,
    });
  }

  // 更新refresh Token信息
  if (accessToken) {
    dispatch({
      type: ReducersEnum.SET_TOKEN,
      payload: {
        accessToken,
      },
    });
  }

  return resData || {};
};

// 请求后 - 错误处理
export const onResError = (error: AxiosError, isNest = false) => {
  if (!error) return;
  const { dispatch } = getDvaApp()._store;
  const config = error?.config;
  const response = error?.response;
  const url = config?.url;
  const reqId = config?.headers['X-Request-Id'];
  let description;

  if (isNest) {
    const errorCode = response?.data?.code;
    description =
      intlGetNestApiError(errorCode) || intlGet('API.ERROR.CODE.NEST.ERROR');
  } else {
    description = intlGetApiError(response?.status);
  }

  if (!description) {
    description = intlGet('API.ERROR.REQ.WAS.ABORTED', {
      ...error,
      url: url?.split('?')?.[0] || url,
      reqId,
    });
  }

  const currentPageUrl = window.location.href;
  const isDiscordAuthorizePage = currentPageUrl.includes('/discordAuthorize');

  // discord认证页面不需要错误提醒
  if (!isDiscordAuthorizePage) {
    notification.error({ description });
  }

  if (
    isNest &&
    error.status === 401 &&
    !whiteList.includes(location.pathname)
  ) {
    dispatch({
      type: ReducersEnum.ON_LOG_OUT,
    });
  }

  const status = response?.status;
  if (isNest && status && nestWhiteCode.includes(status)) {
    return;
  }
  // 请求错误上报
  burialPoint.reportEvent({
    moduleName: 'requestFailed',
    eventType: EventTypeEnum.REQUEST_EVENT,
    eventName: eventNameEnum[11],
    msg: `请求异常：${error.toString()}`,
    exts: {
      url: response?.config?.url,
      data: response?.config?.data,
      headers: response?.config?.headers,
    },
  });
};

export const defaultPageParams = (
  data?: Record<any, any>,
): Record<any, any> => ({
  pageNum: 1,
  pageSize: 10,
  ...data,
});

// 改写分页参数
export const rewritePagingParams = (
  data: Record<string, any> | any,
): Record<string, any> => {
  const newParams = {
    ...data,
    pageNum: +data?.pageIndex || 1,
  };
  delete newParams?.pageIndex;
  return newParams;
};

// 处理返回结果
export function handleReturnResults({
  params = {},
  isDefaultError = true,
  successCallBack,
  errorCallBack,
}: {
  params: Record<string, any>;
  successCallBack: (data: any) => void;
  isDefaultError?: boolean;
  errorCallBack?: (data: any) => void;
}): void {
  let { code } = params;

  if (code === 0) {
    try {
      successCallBack?.(params);
    } catch (e) {
      console.error(e);
    }
  } else {
    switch (code) {
      case 20016:
        // 无demo权限操作
        history.push(
          `${RouterPath.NO_PERMISSION}?${onSerialize({
            headerTitle: intlGet('COMMON.NO.PERMISSION.HEADERS.TITLE'),
            isShowBack: BooleanTypeEnum.Y,
            type: 'demo',
          })}`,
        );
        break;
      default:
        if (isDefaultError) {
          // 如果没有对应code的转义，则code等于UNKNOWN（未知）
          if (code && !intlGetApiError(code)) {
            code = 'UNKNOWN';
            params.code = 'UNKNOWN';
          }
          const description = intlGetApiError(code);
          if (description) {
            notification.error({ description });
          }
        }
        break;
    }

    errorCallBack?.(params);
  }
}
